import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot, Camera, Check, Keyboard, Mic, MicOff, MousePointerClick,
  Navigation, Pause, Play, ScanFace, Volume2, X, ShieldCheck, AlertTriangle
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  aliasesForAction,
  expandStrategyTerms,
  findActionByBrain,
  findLearnedResponse,
  findPageByBrain,
  mergeCommandBrain,
} from "../utils/nexoCommandBrain";
import nexoLearning, {
  isCorrectionUtterance, extractCorrectedCommand, recordCorrection,
  lookupCorrection, pushTranscriptHistory, previousTranscript,
  shouldEscalateToManualEntry, recordRecognitionFailure,
  isChainedCommand, splitCommandChain, runCommandChain,
  isRepeatWithSubstitution, recordLastCommand, buildSubstitutedCommand,
  recordVoiceVerificationResult, checkStartupAnomalies, trackSuccessfulVocabulary,
} from "../utils/nexoSelfLearningEngine";

const TOKEN_KEY = "ct_login_token";
const PAGE_CONTEXT_KEY = "nexo_active_page_context_v1";
const AGENT_SESSION_KEY = "nexo_agent_should_run_v1";
const AGENT_FACE_VERIFIED_KEY = "nexo_agent_face_verified_v1";
const INTERNAL_CONTEXT_KEY = "nexo_internal_context_v1";
const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
void nexoLearning;

// ─── Page Registry ────────────────────────────────────────────────────────────
const PAGE_ALIASES = [
  { keys: ["dashboard", "home", "main"], path: "/" },
  { keys: ["nexo chat", "nexo groups", "groups chat", "nexo"], path: "/nexo" },
  { keys: ["nexo assistant", "assistant", "voice assistant"], path: "/nexo-assistant" },
  { keys: ["bloomberg watch", "bloomberg"], path: "/bloomberg-watch" },
  { keys: ["bloomberg terminal", "terminal"], path: "/bloomberg-terminal" },
  { keys: ["option decay", "options"], path: "/option-decay" },
  { keys: ["expiry decay", "expiry"], path: "/expiry-decay-desk" },
  { keys: ["market reaction", "market"], path: "/market-reaction" },
  { keys: ["strike market reaction", "strike market"], path: "/strike-market-reaction" },
  { keys: ["stocks"], path: "/stocks" },
  { keys: ["indicators"], path: "/indicators" },
  { keys: ["world monitor", "world"], path: "/world-monitor" },
  { keys: ["accounts"], path: "/accounts" },
  { keys: ["orders"], path: "/orders" },
  { keys: ["positions"], path: "/Positions" },
  { keys: ["secret", "x portal"], path: "/x-portal" },
  { keys: ["dhan stocks", "dhan"], path: "/dhan-stocks" },
  { keys: ["bank nifty", "banknifty"], path: "/banknifty" },
  { keys: ["bankex"], path: "/bankex" },
  { keys: ["jammu"], path: "/jammu" },
];

function pathLabel(path) {
  const match = PAGE_ALIASES.find(p => p.path === path);
  return match?.keys?.[0] || path;
}

function pageForPath(path) {
  return PAGE_ALIASES.find(page => page.path === path) || { keys: [pathLabel(path)], path };
}

// ─── Speech Recognition ───────────────────────────────────────────────────────
function createRecognition() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  r.continuous = true;
  r.interimResults = true;
  r.lang = navigator.language?.startsWith("en") ? navigator.language : "en-IN";
  r.maxAlternatives = 5;
  return r;
}

// ─── Text Utilities ───────────────────────────────────────────────────────────
function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^\w\s./',-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stripCommandWord(raw, ...words) {
  let s = String(raw || "");
  for (const w of words) {
    s = s.replace(new RegExp(`^\\s*${w}\\s+`, "i"), "");
  }
  return s.trim();
}

function stripFillers(text) {
  return normalize(text)
    .replace(/\b(hey|nexo|boss|please|can you|could you|would you|i want to|i need to|i want|go ahead and|just|now|the|a|an|this|that|my)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function correctSpokenText(text, brain = null) {
  let corrected = normalize(text);
  SPOKEN_WORD_CORRECTIONS.forEach(([pattern, replacement]) => {
    corrected = corrected.replace(pattern, replacement);
  });

  const trainedTerms = [
    ...(brain?.pageAliases || []).flatMap(item => [item.label, ...(item.aliases || [])]),
    ...(brain?.strategyTerms || []).flatMap(item => [item.term, ...(item.aliases || [])]),
    ...(brain?.learnedTerms || []).flatMap(item => [item.term, item.label, ...(item.aliases || [])]),
    ...(brain?.pageSnapshots || []).flatMap(item => [...(item.controls || []), ...(item.metrics || [])]),
    ...visibleControlLabels(),
  ].map(normalize).filter(Boolean);

  trainedTerms.forEach(term => {
    if (term === "oi" || term.includes("oi ")) {
      corrected = corrected
        .replace(/\b(okay|ok|oh|o|oy|i|eye)\b(?=\s+analysis)/g, "oi")
        .replace(/\bo\s*i\b/g, "oi");
    }

    const correctedWords = corrected.split(" ");
    const termWords = term.split(" ");
    if (termWords.length >= 2) {
      for (let i = 0; i <= correctedWords.length - termWords.length; i++) {
        const windowText = correctedWords.slice(i, i + termWords.length).join(" ");
        if (phraseSimilarity(windowText, term) >= 0.78) {
          correctedWords.splice(i, termWords.length, ...termWords);
          corrected = correctedWords.join(" ");
        }
      }
    }
  });

  return corrected.replace(/\s+/g, " ").trim();
}

function speechIntentScore(text, brain = null) {
  const corrected = correctSpokenText(text, brain);
  const terms = [
    ...(brain?.pageAliases || []).flatMap(item => [item.label, ...(item.aliases || [])]),
    ...(brain?.actionAliases || []).flatMap(item => [item.action, ...(item.aliases || [])]),
    ...(brain?.strategyTerms || []).flatMap(item => [item.term, ...(item.aliases || [])]),
    ...(brain?.learnedTerms || []).flatMap(item => [item.term, item.label, ...(item.aliases || [])]),
    ...visibleControlLabels(),
  ].map(normalize).filter(Boolean);

  const bestTerm = terms.reduce((best, term) => Math.max(best, phraseSimilarity(corrected, term)), 0);
  const commandBoost = /\b(open|click|select|show|start|stop|read|go|shift|switch|analysis|oi|itm|nifty|sensex)\b/.test(corrected) ? 0.15 : 0;
  return bestTerm + commandBoost;
}

function chooseBestSpeechAlternative(alternatives, brain = null) {
  const options = alternatives
    .map(alt => ({
      transcript: alt?.transcript || "",
      confidence: Number(alt?.confidence || 0),
    }))
    .filter(item => item.transcript.trim());
  if (!options.length) return "";

  return options
    .map(item => ({
      ...item,
      score: item.confidence * 0.45 + speechIntentScore(item.transcript, brain) * 0.55,
    }))
    .sort((a, b) => b.score - a.score)[0].transcript;
}

function levenshtein(a, b) {
  const left = normalize(a);
  const right = normalize(b);
  if (left === right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;
  const prev = Array.from({ length: right.length + 1 }, (_, i) => i);
  const curr = new Array(right.length + 1);
  for (let i = 1; i <= left.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= right.length; j++) {
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
      );
    }
    for (let j = 0; j < curr.length; j++) prev[j] = curr[j];
  }
  return prev[right.length];
}

function phraseSimilarity(a, b) {
  const left = normalize(a);
  const right = normalize(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) return 0.86;
  const leftWords = left.split(" ").filter(Boolean);
  const rightWords = right.split(" ").filter(Boolean);
  const wordHits = leftWords.filter(w =>
    rightWords.some(rw => rw === w || rw.includes(w) || w.includes(rw) || 1 - levenshtein(w, rw) / Math.max(w.length, rw.length) >= 0.72)
  ).length;
  const wordScore = wordHits / Math.max(leftWords.length, rightWords.length, 1);
  const editScore = 1 - levenshtein(left, right) / Math.max(left.length, right.length, 1);
  return Math.max(wordScore * 0.85, editScore);
}

function elementLabel(el) {
  return normalize([
    el.innerText,
    el.textContent,
    el.getAttribute?.("aria-label"),
    el.getAttribute?.("title"),
    el.getAttribute?.("placeholder"),
    el.getAttribute?.("data-label"),
    el.getAttribute?.("name"),
    el.value,
    el.getAttribute?.("alt"),
  ].filter(Boolean).join(" ")).slice(0, 180);
}

function visibleControlLabels() {
  if (typeof document === "undefined") return [];
  return Array.from(document.querySelectorAll([
    "button", "a[href]", "[role='button']", "[role='tab']", "[role='menuitem']",
    "[role='option']", "[role='link']", "[role='combobox']", "[role='listbox']",
    "select", "option", "label", "summary", "[data-tab]", "[aria-controls]",
    "[class*='tab']", "[class*='button']", "[data-action]", "[tabindex]"
  ].join(", ")))
    .filter(el => el.tagName?.toLowerCase() === "option" || isElementVisible(el))
    .map(elementLabel)
    .filter(label => label && label.length <= 120);
}

// ─── DOM Interaction ──────────────────────────────────────────────────────────
function activeTextTarget() {
  const el = document.activeElement;
  if (!el) return null;
  const tag = el.tagName?.toLowerCase();
  if (tag === "input" && !["submit","button","checkbox","radio","file","image"].includes(el.type)) return el;
  if (tag === "textarea") return el;
  if (el.isContentEditable) return el;
  return null;
}

function setTargetValue(target, text, append = true) {
  if (!target) return false;
  const tag = target.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea") {
    const next = append ? `${target.value || ""}${text}` : text;
    // React synthetic event trigger
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      tag === "input" ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype,
      "value"
    )?.set;
    nativeInputValueSetter?.call(target, next);
    target.dispatchEvent(new Event("input", { bubbles: true }));
    target.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }
  if (target.isContentEditable) {
    target.textContent = append ? `${target.textContent || ""}${text}` : text;
    target.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }
  return false;
}

function submitNearestForm(target) {
  // Try form submit button
  if (target?.form) {
    const btn = target.form.querySelector("button[type='submit'], input[type='submit']");
    if (btn && !btn.disabled) { btn.click(); return true; }
    target.form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    return true;
  }
  // Generic submit/send button scan
  const candidates = Array.from(document.querySelectorAll(
    "button[type='submit'], input[type='submit'], " +
    "button[aria-label], button[title]"
  ));
  const sendBtn = candidates.find(b => {
    const text = normalize(
      b.innerText || b.textContent || b.getAttribute("aria-label") || b.getAttribute("title") || b.value || ""
    );
    return !b.disabled && (
      text.includes("send") || text.includes("submit") || text.includes("post") ||
      text.includes("save") || text.includes("confirm") || text.includes("ok")
    );
  });
  if (sendBtn) { sendBtn.click(); return true; }
  // Enter keypress on target
  if (target) {
    target.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", code: "Enter", keyCode: 13, bubbles: true }));
    target.dispatchEvent(new KeyboardEvent("keyup", { key: "Enter", code: "Enter", keyCode: 13, bubbles: true }));
    return true;
  }
  return false;
}

function isElementVisible(el) {
  if (!el) return false;
  if (el.disabled || el.getAttribute("aria-disabled") === "true") return false;

  let node = el;
  while (node && node.nodeType === 1) {
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
      return false;
    }
    node = node.parentElement;
  }

  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function clickVisibleElement(searchText) {
  const wanted = stripFillers(searchText)
    .replace(/\b(click|press|tap|hit|select)\b/g, "")
    .replace(/\b(button|link|tab|menu|control)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!wanted || wanted.length < 1) return false;

  const selectors = [
    "button", "a[href]", "[role='button']", "[role='tab']", "[role='menuitem']",
    "[role='switch']", "[role='checkbox']", "[role='option']", "[role='link']",
    "input[type='button']", "input[type='submit']", "input[type='checkbox']",
    "input[type='radio']", "label", "summary", ".btn", "[class*='button']",
    "[class*='tab']", "[class*='toggle']", "[class*='switch']", "[onclick]",
    "[data-action]", "[tabindex]"
  ];

  const elements = Array.from(new Set(document.querySelectorAll(selectors.join(", "))));

  const scored = elements
    .filter(el => {
      return isElementVisible(el);
    })
    .map(el => {
      const rawLabel = [
        el.innerText,
        el.textContent,
        el.getAttribute("aria-label"),
        el.getAttribute("title"),
        el.getAttribute("placeholder"),
        el.getAttribute("data-label"),
        el.getAttribute("name"),
        el.value,
        el.getAttribute("alt"),
      ].filter(Boolean).join(" ");
      const label = normalize(rawLabel).slice(0, 140);

      if (!label) return { el, score: 0 };

      let score = 0;
      if (label === wanted) score = 100;
      else if (label.startsWith(wanted)) score = 88;
      else if (label.includes(wanted)) score = 76;
      else if (wanted.includes(label) && label.length > 2) score = 62;
      else {
        const wantedWords = wanted.split(" ").filter(w => w.length > 2);
        const labelWords = label.split(" ");
        const matchedWords = wantedWords.filter(w =>
          labelWords.some(lw => lw.includes(w) || w.includes(lw))
        );
        score = wantedWords.length ? (matchedWords.length / wantedWords.length) * 58 : 0;
      }
      return { el, score, label };
    })
    .filter(item => item.score >= 30)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return false;
  const best = scored[0];
  best.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  best.el.focus?.();
  best.el.click();
  return best.label || true;
}

function visibleControlCandidates() {
  const selectors = [
    "button", "a[href]", "[role='button']", "[role='tab']", "[role='menuitem']",
    "[role='switch']", "[role='checkbox']", "[role='option']", "[role='link']",
    "[role='combobox']", "[role='listbox']", "select", "option",
    "input[type='button']", "input[type='submit']", "input[type='checkbox']",
    "input[type='radio']", "label", "summary", ".btn", "[class*='button']",
    "[class*='tab']", "[class*='toggle']", "[class*='switch']", "[onclick]",
    "[data-action]", "[tabindex]"
  ];

  return Array.from(new Set(document.querySelectorAll(selectors.join(", "))))
    .filter(el => el.tagName?.toLowerCase() === "option" || isElementVisible(el))
    .map(el => ({ el, label: elementLabel(el) }))
    .filter(item => item.label);
}

function findBestVisibleControl(searchText, minimum = 0.52) {
  const wanted = stripFillers(searchText)
    .replace(/\b(click|press|tap|hit|select|choose|pick|open|show|go to|switch to|shift to)\b/g, "")
    .replace(/\b(button|link|tab|menu|control|dropdown|drop down|option|internal)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!wanted) return null;

  return visibleControlCandidates()
    .map(item => ({ ...item, score: phraseSimilarity(wanted, item.label) }))
    .filter(item => item.score >= minimum)
    .sort((a, b) => b.score - a.score || a.label.length - b.label.length)[0] || null;
}

function routeHrefForElement(el) {
  const href = el.closest?.("a[href]")?.getAttribute("href") || el.getAttribute?.("href") || "";
  return href && !href.startsWith("http") && !href.startsWith("//") ? href : "";
}

function controlMatchScore(wanted, item, options = {}) {
  const wantedWords = normalize(wanted).split(" ").filter(Boolean);
  const labelWords = normalize(item.label).split(" ").filter(Boolean);
  const wantedNorm = normalize(wanted);
  const labelNorm = normalize(item.label);
  let score = phraseSimilarity(wanted, item.label);

  if (wantedNorm === "nifty" && /\bbank\s*nifty\b|\bbanknifty\b/.test(labelNorm)) score -= 1.2;
  if ((wantedNorm === "banknifty" || wantedNorm === "bank nifty") && labelNorm.includes("bank")) score += 0.5;
  if (wantedNorm === "sensex" && labelNorm.includes("sensex")) score += 0.45;
  if (wantedNorm === "bankex" && labelNorm.includes("bankex")) score += 0.45;

  if (labelWords.includes(wantedNorm)) score += 0.55;
  if (labelWords[0] === wantedNorm) score += 0.35;
  if (wantedWords.length && wantedWords.every(word => labelWords.includes(word))) score += 0.28;
  if (item.el.getAttribute?.("role") === "tab") score += 0.18;
  if (/\btab\b/i.test(item.el.className || "") || item.el.getAttribute?.("data-tab")) score += 0.14;
  if (["BUTTON", "DIV", "SPAN"].includes(item.el.tagName || "")) score += 0.05;

  const href = routeHrefForElement(item.el);
  if (href && href !== window.location.pathname) score -= options.penalizeRouteLinks === false ? 0 : 0.45;
  if (href && href === window.location.pathname) score += 0.08;
  return score;
}

function clickVisibleElementFuzzy(searchText, minimum = 0.52) {
  const exact = clickVisibleElement(searchText);
  if (exact) return exact;
  const best = findBestVisibleControl(searchText, minimum);
  if (!best) return false;
  const tag = best.el.tagName?.toLowerCase();
  if (tag === "option" && best.el.parentElement?.tagName?.toLowerCase() === "select") {
    const select = best.el.parentElement;
    select.value = best.el.value;
    select.dispatchEvent(new Event("input", { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
  } else {
    best.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    best.el.focus?.();
    best.el.click();
  }
  return best.label || true;
}

function selectDropdownOption(searchText) {
  const wanted = stripFillers(searchText)
    .replace(/\b(select|choose|pick|set|open|show|dropdown|drop down|option|date|expiry)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!wanted) return false;

  const nativeSelects = Array.from(document.querySelectorAll("select")).filter(isElementVisible);
  for (const select of nativeSelects) {
    const options = Array.from(select.options || []);
    const best = options
      .map(option => ({
        option,
        label: normalize(option.textContent || option.label || option.value),
        score: phraseSimilarity(wanted, option.textContent || option.label || option.value),
      }))
      .filter(item => item.score >= 0.48)
      .sort((a, b) => b.score - a.score)[0];
    if (best) {
      select.value = best.option.value;
      select.dispatchEvent(new Event("input", { bubbles: true }));
      select.dispatchEvent(new Event("change", { bubbles: true }));
      return best.label || true;
    }
  }

  const opener = Array.from(document.querySelectorAll("[role='combobox'], [aria-haspopup='listbox'], [aria-haspopup='menu'], button, [role='button']"))
    .filter(isElementVisible)
    .find(el => /\b(select|expiry|date|choose|dropdown|drop down)\b/.test(elementLabel(el)) || el.getAttribute("aria-expanded") !== null);
  opener?.click?.();

  const bestOption = findBestVisibleControl(wanted, 0.48);
  if (bestOption) {
    bestOption.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    bestOption.el.click();
    return bestOption.label || true;
  }

  return false;
}

function clickInternalTab(searchText) {
  const wanted = stripFillers(searchText)
    .replace(/\b(open|go to|switch to|shift to|move to|select|click|press|tap|under|inside|internal|tab|section)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!wanted) return false;

  const tabs = Array.from(document.querySelectorAll("[role='tab'], button, [role='button'], [class*='tab'], [data-tab], [aria-controls]"))
    .filter(isElementVisible)
    .map(el => ({ el, label: elementLabel(el) }))
    .filter(item => item.label)
    .map(item => ({ ...item, score: controlMatchScore(wanted, item, { penalizeRouteLinks: true }) }))
    .filter(item => item.score >= 0.5)
    .sort((a, b) => b.score - a.score || a.label.length - b.label.length);

  if (!tabs.length) return false;
  tabs[0].el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  tabs[0].el.focus?.();
  tabs[0].el.click();
  rememberInternalContext([tabs[0].label]);
  return tabs[0].label || true;
}

function activeInternalContextLabel() {
  const active = Array.from(document.querySelectorAll(
    "[aria-selected='true'], [aria-current='page'], [role='tab'][data-state='active'], .active, [class*='active'], [class*='selected']"
  ))
    .filter(isElementVisible)
    .map(elementLabel)
    .filter(label => label && label.length <= 80 && !/\b(start|stop|submit|save|delete)\b/.test(label));

  const remembered = readInternalContext();
  return Array.from(new Set([...active, ...remembered])).slice(-5).join(" / ");
}

function clickVisibleButtonByKeywords(keywords, options = {}) {
  const words = keywords.map(normalize).filter(Boolean);
  const elements = Array.from(new Set(document.querySelectorAll([
    "button",
    "a[href]",
    "[role='button']",
    "[role='tab']",
    "input[type='button']",
    "input[type='submit']",
    "[onclick]",
    "[tabindex]",
  ].join(", "))));

  const scored = elements
    .filter(isElementVisible)
    .map(el => {
      const label = normalize([
        el.innerText,
        el.textContent,
        el.getAttribute("aria-label"),
        el.getAttribute("title"),
        el.value,
      ].filter(Boolean).join(" "));

      if (!label) return { el, label, score: 0 };

      let score = 0;
      for (const word of words) {
        if (label === word) score += 80;
        else if (label.includes(word)) score += 45;
        else if (word.includes(label) && label.length > 2) score += 20;
      }

      if (options.preferStart && /\b(start|▶)\b/.test(label)) score += 40;
      if (options.rejectStop && /\b(stop|■)\b/.test(label)) score -= 80;

      return { el, label, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return false;

  const best = scored[0];
  best.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  best.el.focus?.();
  best.el.click();
  return best.label || true;
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function readStoredPageContext() {
  try {
    const saved = JSON.parse(localStorage.getItem(PAGE_CONTEXT_KEY) || "null");
    if (saved?.path) return saved;
  } catch {}
  return null;
}

function shouldResumeAgent() {
  return localStorage.getItem(AGENT_SESSION_KEY) === "1";
}

function hasSessionFaceVerification() {
  return localStorage.getItem(AGENT_FACE_VERIFIED_KEY) === "1";
}

function readInternalContext() {
  try {
    const saved = JSON.parse(localStorage.getItem(INTERNAL_CONTEXT_KEY) || "null");
    return Array.isArray(saved?.labels) ? saved.labels.filter(Boolean).slice(0, 5) : [];
  } catch {
    return [];
  }
}

function rememberInternalContext(labels) {
  const clean = labels
    .map(label => normalize(label))
    .filter(label => label && label.length <= 80 && !/\b(start|stop|submit|save|delete|remove)\b/.test(label));
  if (!clean.length) return;
  const merged = Array.from(new Set([...readInternalContext(), ...clean])).slice(-5);
  localStorage.setItem(INTERNAL_CONTEXT_KEY, JSON.stringify({ labels: merged, updatedAt: Date.now() }));
}

function clearInternalContext() {
  localStorage.removeItem(INTERNAL_CONTEXT_KEY);
}

function collectTrainingSnapshot(route) {
  const controls = Array.from(document.querySelectorAll("button, a, [role='button'], input, textarea, select"))
    .map(el => {
      const aria = el.getAttribute("aria-label") || el.getAttribute("title") || el.getAttribute("placeholder") || "";
      const text = el.innerText || el.textContent || el.value || "";
      return normalize(`${aria} ${text}`).slice(0, 80);
    })
    .filter(value => value.length > 1);

  const metrics = collectReadableBlocks()
    .filter(block => /\b(total|volume|sellers|buyers|call|put|ce|pe|oi|pcr|price|ltp|signal|news|feed|alert|data)\b/i.test(block))
    .slice(0, 80)
    .map(block => block.slice(0, 160));

  return {
    route,
    title: document.title || pathLabel(route),
    controls: Array.from(new Set(controls)).slice(0, 60),
    metrics: Array.from(new Set(metrics)).slice(0, 80),
  };
}

function waitForPagePaint() {
  return delay(550);
}

function getPageText(limit = 1200) {
  const priority = [
    document.querySelector("main"),
    document.querySelector("[role='main']"),
    document.querySelector("article"),
    document.querySelector(".content"),
    document.body
  ].find(Boolean);

  return String(priority?.innerText || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit);
}

function isReadRequest(text) {
  return /\b(read|read out|tell me|what|show|give me|explain|describe|information|info|data|details|news|headline|headlines|summary|summarize)\b/.test(text);
}

function isSpecificReadRequest(text) {
  return /\b(read|read out|tell me|what|give me|explain|describe|information|info|data|details|news|headline|headlines|summary|summarize)\b/.test(text) ||
    (/\bshow\b/.test(text) && /\b(news|headline|headlines|data|details|info|information|summary|table|feed|feeds|numbers)\b/.test(text));
}

function extractReadTopic(text) {
  const topic = normalize(text)
    .replace(/\b(hey|nexo|boss|please|can you|could you|would you)\b/g, " ")
    .replace(/\b(read out|read|tell me|show me|show|give me|explain|describe|summarize|summary of|what is|what are|what does|what's|information about|info about|details about|data about)\b/g, " ")
    .replace(/\b(on this page|from this page|in this page|this page|page|the|a|an|about|of|for|me)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return topic;
}

function visibleTextOf(element) {
  if (!element) return "";
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return "";
  return String(element.innerText || element.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

function uniqueTextBlocks(blocks) {
  const seen = new Set();
  return blocks
    .map(block => String(block || "").replace(/\s+/g, " ").trim())
    .filter(block => {
      if (!block || block.length < 8) return false;
      const key = block.toLowerCase().slice(0, 180);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function collectReadableBlocks() {
  const selectors = [
    "article",
    "section",
    "main",
    "table",
    "tbody tr",
    "[role='article']",
    "[role='row']",
    "[class*='news']",
    "[class*='headline']",
    "[class*='alert']",
    "[class*='feed']",
    "[class*='data']",
    "[class*='card']",
    "[class*='panel']",
    "[class*='table']",
    "[class*='row']",
    "li",
  ];

  return uniqueTextBlocks(
    Array.from(document.querySelectorAll(selectors.join(", ")))
      .map(visibleTextOf)
      .filter(text => text.length >= 12 && text.length <= 1800)
  );
}

function scoreReadableBlock(block, topicWords, genericType) {
  const text = normalize(block);
  let score = 0;

  for (const word of topicWords) {
    if (text.includes(word)) score += word.length > 4 ? 28 : 18;
  }

  if (genericType === "news" && /\b(news|headline|headlines|breaking|alert|bloomberg|market update|update)\b/.test(text)) {
    score += 35;
  }

  if (genericType === "data" && /\b(data|price|ltp|change|volume|oi|open interest|high|low|close|signal|feed|index|market)\b/.test(text)) {
    score += 35;
  }

  if (/\d/.test(text)) score += genericType === "data" ? 18 : 6;
  if (block.length > 60) score += 6;

  return score;
}

function extractNearbyMetric(labelWords) {
  const words = labelWords.map(normalize).filter(Boolean);
  const blocks = collectReadableBlocks();
  const scored = blocks
    .map(block => {
      const normalized = normalize(block);
      const score = words.reduce((total, word) => total + (normalized.includes(word) ? 1 : 0), 0);
      return { block, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.block.length - b.block.length);

  return scored[0]?.block || "";
}

function getGenericMetricText(rawCommand, limit = 1200) {
  const text = normalize(rawCommand);
  const topic = extractReadTopic(rawCommand);
  const wanted = [];

  if (/\b(total volume|volume|vol)\b/.test(text)) {
    wanted.push(["total volume", "volume"]);
    wanted.push(["total ce volume", "ce volume", "call volume"]);
    wanted.push(["total pe volume", "pe volume", "put volume"]);
  }

  if (/\b(call sellers|call seller|ce sellers|ce seller|call writers|ce writers)\b/.test(text)) {
    wanted.push(["total ce sellers", "call sellers", "ce sellers", "call writers"]);
  }

  if (/\b(put sellers|put seller|pe sellers|pe seller|put writers|pe writers)\b/.test(text)) {
    wanted.push(["total pe sellers", "put sellers", "pe sellers", "put writers"]);
  }

  if (/\b(open interest|oi)\b/.test(text)) {
    wanted.push(["total ce open interest", "ce open interest", "call open interest", "ce oi"]);
    wanted.push(["total pe open interest", "pe open interest", "put open interest", "pe oi"]);
  }

  if (/\b(pcr|ratio)\b/.test(text)) {
    wanted.push(["pcr ratio", "pcr"]);
  }

  if (/\b(price|ltp|spot|strike|support|resistance|signal|bias|sentiment|feed|feeds|news|headline|alert)\b/.test(text)) {
    wanted.push([topic || text]);
  }

  const metricBlocks = uniqueTextBlocks(wanted.map(extractNearbyMetric).filter(Boolean));
  if (metricBlocks.length) return metricBlocks.join(". ").slice(0, limit);

  return "";
}

function actionTargetFromCommand(rawCommand) {
  return stripFillers(rawCommand)
    .replace(/\b(start|begin|run|launch|activate|enable|turn on|stop|pause|halt|disable|turn off|click|press|tap|open|go|move|navigate|shift|switch)\b/g, " ")
    .replace(/\b(to|on|in|under|inside|page|tab|screen|desk|terminal|button|control)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isPageActionCommand(text, brain = null) {
  const action = brain ? findActionByBrain(brain, text) : "";
  return ["start", "stop"].includes(action) || /\b(start|begin|run|launch|activate|enable|turn on|stop|pause|halt|disable|turn off)\b/.test(text);
}

function isStopPageAction(text, brain = null) {
  return (brain && findActionByBrain(brain, text) === "stop") || /\b(stop|pause|halt|disable|turn off)\b/.test(text);
}

async function performPageAction(rawCommand, contextPage, brain = null) {
  const text = normalize(rawCommand);
  const target = actionTargetFromCommand(rawCommand);
  const stop = isStopPageAction(text, brain);
  const primary = stop
    ? Array.from(new Set(["stop", "■ stop", "pause", "halt", ...aliasesForAction(brain, "stop")]))
    : Array.from(new Set(["start", "▶ start", "run", "launch", "activate", "enable", ...aliasesForAction(brain, "start")]));
  const targetWords = target ? [target] : [];

  let clicked = false;

  if (targetWords.length) {
    clicked = clickVisibleButtonByKeywords([...targetWords, ...primary], {
      preferStart: !stop,
      rejectStop: !stop,
    });
  }

  if (!clicked) {
    clicked = clickVisibleButtonByKeywords(primary, {
      preferStart: !stop,
      rejectStop: !stop,
    });
  }

  if (!clicked && target) clicked = clickVisibleElement(`${stop ? "stop" : "start"} ${target}`);
  if (!clicked) clicked = clickVisibleElement(stop ? "stop" : "start");

  return {
    clicked,
    action: stop ? "stop" : "start",
    target: target || pathLabel(contextPage?.path || window.location.pathname),
  };
}

function commandControlSequence(rawCommand, brain = null) {
  const text = correctSpokenText(rawCommand, brain);
  const actionWords = new Set([
    "start", "begin", "run", "launch", "activate", "enable", "turn", "on",
    "stop", "pause", "halt", "disable", "off", "click", "press", "tap",
    "open", "go", "to", "switch", "shift", "move", "navigate", "page",
    "tab", "internal", "under", "inside", "button", "control",
  ]);
  const pageWords = PAGE_ALIASES.flatMap(page => page.keys).join(" ");
  const candidates = visibleControlCandidates()
    .filter(item => !/\b(start|stop|pause|run|launch|activate|enable|submit|send|save)\b/.test(item.label))
    .map(item => {
      const labelWords = item.label.split(" ").filter(w => w.length > 1 && !actionWords.has(w));
      const hit = labelWords.some(w => text.includes(w));
      const directIndex = text.indexOf(item.label);
      const score = directIndex >= 0 ? 1 : phraseSimilarity(text, item.label);
      return { ...item, hit, directIndex, score };
    })
    .filter(item => item.hit || item.score >= 0.48)
    .filter(item => !pageWords.includes(item.label) || text.includes(item.label))
    .sort((a, b) => {
      const ai = a.directIndex >= 0 ? a.directIndex : 9999;
      const bi = b.directIndex >= 0 ? b.directIndex : 9999;
      return ai - bi || b.score - a.score || a.label.length - b.label.length;
    });

  const seen = new Set();
  return candidates.filter(item => {
    const key = item.label;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 3);
}

async function clickCommandControlSequence(rawCommand, brain = null) {
  const sequence = commandControlSequence(rawCommand, brain);
  const clicked = [];
  for (const item of sequence) {
    item.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
    item.el.focus?.();
    item.el.click();
    clicked.push(item.label);
    await delay(250);
  }
  rememberInternalContext(clicked);
  return clicked;
}

function getExpiryDecayMetricText(rawCommand, limit = 1200) {
  const text = normalize(rawCommand);
  if (!/\b(expiry|decay|sensex|itm|seller|sellers|volume|oi|open interest|call|put|ce|pe)\b/.test(text)) {
    return "";
  }

  const wanted = [];

  if (/\b(total volume|volume)\b/.test(text)) {
    wanted.push(["total ce volume", "ce volume", "call volume"]);
    wanted.push(["total pe volume", "pe volume", "put volume"]);
  }

  if (/\b(call sellers|call seller|ce sellers|ce seller|total call|total ce)\b/.test(text)) {
    wanted.push(["total ce sellers", "call sellers", "ce sellers", "call writers"]);
  }

  if (/\b(put sellers|put seller|pe sellers|pe seller|total put|total pe)\b/.test(text)) {
    wanted.push(["total pe sellers", "put sellers", "pe sellers", "put writers"]);
  }

  if (/\b(call\/put|call put|ce pe|seller data|sellers data|total sellers)\b/.test(text)) {
    wanted.push(["total ce sellers", "call sellers", "ce sellers", "call writers"]);
    wanted.push(["total pe sellers", "put sellers", "pe sellers", "put writers"]);
  }

  if (!wanted.length && /\b(data|details|summary|read|tell me|what)\b/.test(text)) {
    wanted.push(["total ce volume", "ce volume"]);
    wanted.push(["total pe volume", "pe volume"]);
    wanted.push(["total ce sellers", "call sellers", "ce sellers"]);
    wanted.push(["total pe sellers", "put sellers", "pe sellers"]);
  }

  const results = uniqueTextBlocks(wanted.map(extractNearbyMetric).filter(Boolean));
  if (!results.length) return "";

  return `Sensex ITM terminal data. ${results.join(". ")}`.slice(0, limit);
}

function getSpecificPageText(rawCommand, limit = 1200) {
  const topic = extractReadTopic(rawCommand);
  const normalizedCommand = normalize(rawCommand);
  const genericType = /\b(news|headline|headlines|bloomberg|alert)\b/.test(normalizedCommand)
    ? "news"
    : /\b(data|details|info|information|feed|feeds|price|table|numbers)\b/.test(normalizedCommand)
      ? "data"
      : "";

  const ignoredTopicWords = new Set([
    "news", "headline", "headlines", "data", "details", "info", "information",
    "summary", "summarize", "latest", "current", "visible", "available",
  ]);

  const topicWords = topic
    .split(" ")
    .map(word => word.trim())
    .filter(word => word.length > 2 && !ignoredTopicWords.has(word));

  const blocks = collectReadableBlocks();

  const scored = blocks
    .map(block => ({
      block,
      score: scoreReadableBlock(block, topicWords, genericType),
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length) {
    const chosen = uniqueTextBlocks(scored.slice(0, 6).map(item => item.block));
    const prefix = topic
      ? `Here is the ${genericType || "information"} I found for ${topic}. `
      : `Here is the ${genericType || "information"} I found. `;
    return `${prefix}${chosen.join(". ")}`.slice(0, limit);
  }

  if (topicWords.length) {
    const page = getPageText(Math.max(limit * 2, 2000));
    const sentences = uniqueTextBlocks(page.split(/(?<=[.!?])\s+|(?=\b[A-Z][A-Za-z ]{2,}:)/));
    const matching = sentences.filter(sentence => {
      const s = normalize(sentence);
      return topicWords.some(word => s.includes(word));
    });
    if (matching.length) {
      return `I found this for ${topic}. ${matching.slice(0, 8).join(" ")}`.slice(0, limit);
    }
  }

  const fallback = getPageText(limit);
  if (fallback && (genericType || topic)) {
    return `I could not isolate a specific ${topic || genericType} section, so I will read the visible page content. ${fallback}`.slice(0, limit);
  }

  return fallback;
}

function findNavDestination(text, brain = null) {
  const brainMatch = findPageByBrain(brain, text);
  if (brainMatch?.path) {
    return {
      path: brainMatch.path,
      keys: [brainMatch.label, ...(brainMatch.aliases || [])].filter(Boolean),
    };
  }

  const cleaned = normalize(text)
    .replace(/\b(can you|please|nexo|i want to|take me|let's go|lets go)\b/g, "")
    .replace(/^(switch|shift|open|go|move|navigate|jump|load|launch|start|show me)\s*(to\s+|me\s+to\s+)?/, "")
    .replace(/\b(tab|page|screen|section|window|terminal|desk|panel)\b/g, "")
    .trim();

  const direct = PAGE_ALIASES.find(page =>
    page.keys.some(key =>
      normalize(key) === cleaned ||
      cleaned.includes(normalize(key)) ||
      normalize(key).includes(cleaned)
    )
  );
  if (direct) return direct;

  // Fuzzy word match — catches "Sensex terminal" → bloomberg-terminal
  const words = cleaned.split(" ").filter(w => w.length > 2);
  const fuzzy = PAGE_ALIASES.find(page =>
    page.keys.some(key => {
      const keyWords = normalize(key).split(" ");
      return words.some(w => keyWords.some(kw => kw.includes(w) || w.includes(kw)));
    })
  );
  if (fuzzy) return fuzzy;

  const link = Array.from(document.querySelectorAll("a[href]")).find(
    a => normalize(a.innerText || a.textContent).includes(cleaned)
  );
  if (link) {
    const href = link.getAttribute("href");
    if (href && !href.startsWith("http") && !href.startsWith("//")) {
      return { path: href, keys: [cleaned] };
    }
  }
  return null;
}

// ─── Face / Camera Utilities ──────────────────────────────────────────────────
function grabVideoFrame(video) {
  if (!video || !video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d").drawImage(video, 0, 0);
  return canvas;
}

// Replace analyzeFaceCanvas function with this:
async function loadFaceModels() {
  if (!window.faceapi) return false;
  if (window._faceModelsLoaded) return true;
  try {
    const MODEL_URL = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights";
    await Promise.all([
      window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      window.faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
    ]);
    window._faceModelsLoaded = true;
    return true;
  } catch {
    return false;
  }
}

async function analyzeFaceCanvas(canvas) {
  if (!canvas) return { faceScore: 0, eyeScore: 0, status: "failed", detail: "No canvas" };

  const loaded = await loadFaceModels();
  if (loaded && window.faceapi) {
    try {
      const detection = await window.faceapi
        .detectSingleFace(canvas, new window.faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3 }))
        .withFaceLandmarks(true);

      if (detection) {
        const landmarks = detection.landmarks;
        const leftEye = landmarks.getLeftEye();
        const rightEye = landmarks.getRightEye();
        const eyesDetected = leftEye?.length > 0 && rightEye?.length > 0;
        const faceScore = Math.round(detection.detection.score * 100);

        // Eye openness score based on landmark vertical spread
        let eyeScore = 70;
        if (eyesDetected) {
          const eyeHeight = (
            Math.max(...leftEye.map(p => p.y)) - Math.min(...leftEye.map(p => p.y)) +
            Math.max(...rightEye.map(p => p.y)) - Math.min(...rightEye.map(p => p.y))
          ) / 2;
          eyeScore = Math.min(100, Math.round(50 + eyeHeight * 8));
        }

        return {
          faceScore,
          eyeScore,
          status: eyesDetected ? "face_eyes_detected" : "face_detected",
          detail: eyesDetected
            ? `Face detected (${faceScore}% confidence) with both eye landmarks.`
            : `Face detected (${faceScore}% confidence). Eyes not clearly visible.`,
        };
      } else {
        return {
          faceScore: 0,
          eyeScore: 0,
          status: "no_face",
          detail: "No face detected in frame. Ensure good lighting and face the camera.",
        };
      }
    } catch (err) {
      // fallthrough to pixel scoring
    }
  }

  // Pixel quality fallback
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const step = Math.max(4, Math.floor(img.data.length / 40000));
  let brightness = 0, contrast = 0, n = 0;
  for (let i = 0; i < img.data.length; i += 4 * step) {
    const lum = (img.data[i] + img.data[i + 1] + img.data[i + 2]) / 3;
    brightness += lum; contrast += Math.abs(lum - 128); n++;
  }
  brightness = n ? brightness / n : 0;
  contrast = n ? contrast / n : 0;
  const quality = Math.max(0, Math.min(100, Math.round((brightness / 255) * 55 + Math.min(contrast, 80) * 0.55)));
  return {
    faceScore: quality,
    eyeScore: Math.max(0, quality - 12),
    status: quality >= 40 ? "frame_captured" : "low_light",
    detail: "face-api models unavailable. Frame quality score used.",
  };
}

// ─── SENSITIVE COMMANDS (require confirmation) ────────────────────────────────
const SENSITIVE_PATTERN = /\b(delete|remove|logout|log out|trade|order|buy|sell|transfer|clear|reset|wipe)\b/;
const FACE_COMMAND_PATTERN = /\b(start face scan|scan my face|face scan|start eye scan|scan my eyes|take photo|click photo|capture photo|capture face|capture eyes|capture scan|save face scan|eye scan now)\b/;
const FACE_VERIFY_THRESHOLD = 55;
const VOICE_TONE_THRESHOLD = 48;
const VOICE_TONE_PHRASE_GRACE_THRESHOLD = 36;
const SPOKEN_WORD_CORRECTIONS = [
  [/(\bokay|\bok|\boh|\bo)\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\bo\s*i\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\bi\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\beye\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\boy\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\boil\s+(analysis|analytics)\b/g, "oi analysis"],
  [/\bopen interest analysis\b/g, "oi analysis"],
  [/\bopen interest\b/g, "oi"],
  [/\bo\s*i\b/g, "oi"],
  [/\boh\s*i\b/g, "oi"],
  [/\bbank nifty\b/g, "banknifty"],
  [/\bbank nifty terminal\b/g, "banknifty terminal"],
  [/\bsense x\b/g, "sensex"],
  [/\bsens ex\b/g, "sensex"],
  [/\bsensix\b/g, "sensex"],
  [/\bsens sex\b/g, "sensex"],
  [/\bniftie\b/g, "nifty"],
  [/\bexpiry d k\b/g, "expiry decay"],
  [/\bexpirey\b/g, "expiry"],
  [/\bexpairy\b/g, "expiry"],
  [/\bexpiri\b/g, "expiry"],
  [/\bbrake out\b/g, "breakout"],
  [/\bsailer|sailor\b/g, "seller"],
  [/\bsailers|sailors\b/g, "sellers"],
  [/\bitem\b/g, "itm"],
  [/\bi\s*t\s*m\b/g, "itm"],
  [/\bin the money\b/g, "itm"],
  [/\bdon market\b/g, "dhan market"],
  [/\bdone market\b/g, "dhan market"],
  [/\bdon decay\b/g, "dhan decay"],
  [/\bdone decay\b/g, "dhan decay"],
];

function extractVoiceFeatures(time, freq, sampleRate) {
  let rms = 0;
  let crossings = 0;
  for (let i = 0; i < time.length; i++) {
    const v = (time[i] - 128) / 128;
    rms += v * v;
    if (i > 0 && (time[i - 1] - 128) * (time[i] - 128) < 0) crossings++;
  }
  rms = Math.sqrt(rms / time.length);

  let sum = 0;
  let weighted = 0;
  const bands = [0, 0, 0, 0, 0, 0];
  for (let i = 0; i < freq.length; i++) {
    const value = freq[i] / 255;
    const hz = (i * sampleRate) / (2 * freq.length);
    sum += value;
    weighted += hz * value;
    const band = Math.min(bands.length - 1, Math.floor(i / (freq.length / bands.length)));
    bands[band] += value;
  }
  const bandTotal = bands.reduce((a, b) => a + b, 0) || 1;
  return {
    rms,
    zcr: crossings / time.length,
    centroid: sum ? weighted / sum : 0,
    bands: bands.map(v => v / bandTotal),
  };
}

function averageVoiceFeatures(samples) {
  const usable = samples.filter(s => s.rms > 0.01);
  const list = usable.length ? usable : samples;
  if (!list.length) return null;
  const base = { rms: 0, zcr: 0, centroid: 0, bands: [0, 0, 0, 0, 0, 0] };
  list.forEach(sample => {
    base.rms += sample.rms;
    base.zcr += sample.zcr;
    base.centroid += sample.centroid;
    sample.bands.forEach((v, i) => { base.bands[i] += v; });
  });
  const count = list.length;
  return {
    rms: base.rms / count,
    zcr: base.zcr / count,
    centroid: base.centroid / count,
    bands: base.bands.map(v => v / count),
  };
}

function voiceToneScore(a, b) {
  if (!a || !b?.bands?.length) return 0;
  const bandDiff = b.bands.reduce((sum, value, index) => sum + Math.abs(value - (a.bands?.[index] || 0)), 0);
  const centroidDiff = Math.abs((a.centroid || 0) - (b.centroid || 0)) / 3500;
  const zcrDiff = Math.abs((a.zcr || 0) - (b.zcr || 0));
  const rmsDiff = Math.abs((a.rms || 0) - (b.rms || 0));
  const penalty = centroidDiff * 35 + zcrDiff * 180 + rmsDiff * 50 + bandDiff * 75;
  return Math.max(0, Math.min(100, Math.round(100 - penalty)));
}

function bestVoiceToneMatch(live, profiles) {
  const enrolled = profiles.filter(profile => profile.active !== false && profile.voiceprint?.bands?.length);
  if (!enrolled.length) return { ok: true, score: 100, label: "no enrolled voice tone" };
  const scored = enrolled
    .map(profile => ({ label: profile.label, score: voiceToneScore(live, profile.voiceprint) }))
    .sort((a, b) => b.score - a.score);
  const best = scored[0] || { score: 0, label: "unknown" };
  return { ...best, ok: best.score >= VOICE_TONE_THRESHOLD };
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function NexoAssistantAgent({ expandedDefault = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Refs for non-reactive fast access
  const recognitionRef = useRef(null);
  const restartRef = useRef(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const voiceMonitorRef = useRef(null);
  const liveVoiceRef = useRef(null);
  const voiceSamplesRef = useRef([]);
  const configRef = useRef({ settings: {}, voiceProfiles: [], faceProfiles: [] });
  const commandBrainRef = useRef(mergeCommandBrain());
  const snapshotTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const commandCountRef = useRef(0);
  const lastInterimRef = useRef({ text: "", at: 0 });
  const verifiedRef = useRef(false);
  const pendingRef = useRef(null);
  const runningRef = useRef(false);
  const activePageRef = useRef(readStoredPageContext() || pageForPath(window.location.pathname));

  // UI State
  const [expanded, setExpanded] = useState(expandedDefault);
  const [running, setRunning] = useState(false);
  const [listening, setListening] = useState(false);
  const [verified, setVerified] = useState(false);
  const [faceReady, setFaceReady] = useState(false);
  const [scanStatus, setScanStatus] = useState("");
  const [config, setConfig] = useState({ settings: {}, voiceProfiles: [], faceProfiles: [] });
  const [commandBrain, setCommandBrain] = useState(mergeCommandBrain());
  const [lastTranscript, setLastTranscript] = useState("");
  const [status, setStatus] = useState("Idle — click the orb to start Nexo");
  const [pendingCommand, setPendingCommand] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [waveActive, setWaveActive] = useState(false);
  const [activePageContext, setActivePageContext] = useState(activePageRef.current);

  const token = localStorage.getItem(TOKEN_KEY);
  // Keep refs in sync
  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { commandBrainRef.current = commandBrain; }, [commandBrain]);
  useEffect(() => { verifiedRef.current = verified; }, [verified]);
  useEffect(() => { pendingRef.current = pendingCommand; }, [pendingCommand]);
  useEffect(() => { runningRef.current = running; }, [running]);
  useEffect(() => {
    const page = pageForPath(location.pathname);
    activePageRef.current = page;
    setActivePageContext(page);
    clearInternalContext();
    localStorage.setItem(PAGE_CONTEXT_KEY, JSON.stringify({ ...page, updatedAt: Date.now() }));
  }, [location.pathname]);

  useEffect(() => {
    if (!token) return undefined;
    clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => {
      const snapshot = collectTrainingSnapshot(location.pathname);
      if (!snapshot.controls.length && !snapshot.metrics.length) return;
      fetch(`${API_BASE}/api/nexo-assistant/command-brain/page-snapshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(snapshot),
      }).catch(() => {});
    }, 1400);
    return () => clearTimeout(snapshotTimerRef.current);
  }, [location.pathname, token]);

  // ── TTS ──────────────────────────────────────────────────────────────────────
  const speak = useCallback((text, priority = false) => {
    if (!window.speechSynthesis || !text) return;
    if (priority) window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = 1.05;
    u.pitch = 0.92;
    u.lang = "en-IN";
    // Prefer a local female voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      v.lang.startsWith("en") && (v.name.includes("Female") || v.name.includes("Samantha") || v.name.includes("Google"))
    );
    if (preferred) u.voice = preferred;
    window.speechSynthesis.speak(u);
  }, []);

  // ── Command Logger ────────────────────────────────────────────────────────────
  const logCommand = useCallback((payload) => {
    if (!token) return;
    fetch(`${API_BASE}/api/nexo-assistant/logs`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ route: window.location.pathname, ...payload }),
    }).catch(() => {});

    setCommandHistory(prev => [
      { ...payload, ts: new Date().toLocaleTimeString() },
      ...prev.slice(0, 19)
    ]);

    // ── SELF-LEARNING HOOKS ──
    if (payload.status === "done") {
      trackSuccessfulVocabulary(payload.transcript || "");

      // Remember this as "last command" for repeat-with-substitution
      if (["navigate", "page_start", "page_stop", "click"].includes(payload.intent)) {
        const action = payload.intent === "navigate" ? "open"
          : payload.intent === "page_start" ? "start"
            : payload.intent === "page_stop" ? "stop"
              : "click";
        recordLastCommand(action, payload.detail, payload.transcript);
      }
    }
  }, [token]);

  // ── Config Loader ─────────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    if (!token) return configRef.current;
    try {
      const res = await fetch(`${API_BASE}/api/nexo-assistant/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setConfig(data);
        configRef.current = data;
        return data;
      }
    } catch {
      setStatus("⚠ Backend unavailable — operating in offline mode");
    }
    return configRef.current;
  }, [token]);

  const loadCommandBrain = useCallback(async () => {
    if (!token) return commandBrainRef.current;
    try {
      const res = await fetch(`${API_BASE}/api/nexo-assistant/command-brain`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        const merged = mergeCommandBrain(data.commandBrain || {});
        setCommandBrain(merged);
        commandBrainRef.current = merged;
        return merged;
      }
    } catch { /* offline fallback keeps defaults */ }
    return commandBrainRef.current;
  }, [token]);

  useEffect(() => { loadConfig(); loadCommandBrain(); }, [loadConfig, loadCommandBrain]);

  useEffect(() => {
    loadFaceModels(); // preload in background silently
  }, []);

  // ── Voice Verification ────────────────────────────────────────────────────────
  const verifyVoice = useCallback((text) => {
    const cfg = configRef.current;
    const profiles = (cfg.voiceProfiles || []).filter(p => p.active !== false && p.phrase?.trim());
    if (!cfg.settings?.requireVoiceProfile || profiles.length === 0) return true;
    const norm = correctSpokenText(text, commandBrainRef.current);
    return profiles.some(p => {
      const phrase = correctSpokenText(p.phrase, commandBrainRef.current);
      return norm.includes(phrase) || phraseSimilarity(norm, phrase) >= 0.72;
    });
  }, []);

  // ── Camera / Face ─────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    if (streamRef.current) return true; // already running
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setFaceReady(true);
      return true;
    } catch (err) {
      setFaceReady(false);
      setScanStatus(`Camera error: ${err.message}`);
      speak("Camera permission denied. Please allow camera access.", true);
      return false;
    }
  }, [speak]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks?.().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setFaceReady(false);
  }, []);

  const startVoiceMonitor = useCallback(async () => {
    if (voiceMonitorRef.current) return true;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return false;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.25;
      source.connect(analyser);

      audioStreamRef.current = stream;
      audioContextRef.current = ctx;
      const time = new Uint8Array(analyser.fftSize);
      const freq = new Uint8Array(analyser.frequencyBinCount);
      voiceMonitorRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(time);
        analyser.getByteFrequencyData(freq);
        const features = extractVoiceFeatures(time, freq, ctx.sampleRate);
        voiceSamplesRef.current = [...voiceSamplesRef.current.slice(-16), features];
        liveVoiceRef.current = averageVoiceFeatures(voiceSamplesRef.current);
      }, 120);
      return true;
    } catch (err) {
      setStatus(`Voice tone monitor unavailable: ${err.message}`);
      return false;
    }
  }, []);

  const stopVoiceMonitor = useCallback(() => {
    if (voiceMonitorRef.current) clearInterval(voiceMonitorRef.current);
    voiceMonitorRef.current = null;
    audioStreamRef.current?.getTracks?.().forEach(track => track.stop());
    audioStreamRef.current = null;
    audioContextRef.current?.close?.().catch(() => {});
    audioContextRef.current = null;
    voiceSamplesRef.current = [];
    liveVoiceRef.current = null;
  }, []);

  const captureFaceScan = useCallback(async (source = "manual") => {
    const ok = await startCamera();
    if (!ok) return false;

    // Wait for video frame to populate
    await new Promise(resolve => setTimeout(resolve, 600));

    const canvas = grabVideoFrame(videoRef.current);
    if (!canvas) {
      setScanStatus("Camera frame not ready. Wait and try again.");
      speak("Camera frame is not ready yet.");
      return false;
    }

    const analysis = await analyzeFaceCanvas(canvas);
    const image = canvas.toDataURL("image/jpeg", 0.82);

    setScanStatus(`${analysis.status} — face ${analysis.faceScore}% / eyes ${analysis.eyeScore}%`);
    speak(`Face scan complete. Face confidence ${analysis.faceScore} percent.`, true);

    const faceVerified = analysis.faceScore >= FACE_VERIFY_THRESHOLD && analysis.status !== "no_face" && analysis.status !== "failed";

    if (faceVerified) {
      setVerified(true);
      verifiedRef.current = true;
      setStatus("✓ Face verified — listening for commands");
    }

    // ── SAVE TO BACKEND ──
    if (token) {
      try {
        const res = await fetch(`${API_BASE}/api/nexo-assistant/face-scans`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            image,
            faceScore: analysis.faceScore,
            eyeScore: analysis.eyeScore,
            status: analysis.status,
            detail: analysis.detail,
            source,
            route: window.location.pathname,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.success) {
          setScanStatus(prev => prev + " — saved ✓");
        } else {
          setScanStatus(prev => prev + ` — save failed: ${data.error || "unknown"}`);
        }
      } catch {
        setScanStatus(prev => prev + " — network error on save");
      }
    }

    logCommand({
      transcript: source === "automatic" ? "auto face scan on start" : "capture face and eyes scan",
      intent: "face_eye_scan",
      status: analysis.status,
      detail: analysis.detail,
    });

    return faceVerified;
  }, [logCommand, speak, startCamera, token]);

  // ── Core Command Executor ─────────────────────────────────────────────────────
  const executeCommand = useCallback(async (rawText) => {
    pushTranscriptHistory(rawText);

    // ── CORRECTION HANDLING ──
    // "no I meant start sensex" → learn the mapping, then run the corrected command
    if (isCorrectionUtterance(rawText)) {
      const corrected = extractCorrectedCommand(rawText);
      const misheard = previousTranscript();
      if (corrected && misheard) {
        recordCorrection(misheard, corrected);
        speak(`Got it. I will remember that ${misheard} means ${corrected}.`, true);
        logCommand({ transcript: rawText, intent: "correction_learned", status: "done", detail: `${misheard} → ${corrected}` });
        return executeCommand(corrected);
      }
    }

    // ── KNOWN CORRECTION SHORTCUT ──
    // If this exact phrase was corrected before, skip straight to the known answer
    const knownCorrection = lookupCorrection(rawText);
    if (knownCorrection && knownCorrection !== normalize(rawText)) {
      return executeCommand(knownCorrection);
    }

    // ── COMMAND CHAINING ──
    // "open expiry decay then start sensex itm then read the totals"
    if (isChainedCommand(rawText)) {
      const steps = splitCommandChain(rawText);
      if (steps.length > 1) {
        speak(`Running ${steps.length} steps in sequence Boss.`, true);
        logCommand({ transcript: rawText, intent: "command_chain", status: "running", detail: steps.join(" → ") });
        await runCommandChain(steps, executeCommand, 900);
        return;
      }
    }

    // ── REPEAT WITH SUBSTITUTION ──
    // "now do the same for banknifty"
    if (isRepeatWithSubstitution(rawText)) {
      const substituted = buildSubstitutedCommand(rawText);
      if (substituted) {
        speak(`Applying that to ${substituted.split(" ").slice(1).join(" ")}.`, true);
        return executeCommand(substituted);
      }
    }

    const brain = commandBrainRef.current;
    const correctedText = correctSpokenText(rawText, brain);
    const text = normalize(correctedText);
    const trainedText = expandStrategyTerms(brain, correctedText);

    const say = (msg, statusMsg) => {
      speak(msg, true);
      setStatus(statusMsg || msg);
    };

    let result = { intent: "unknown", status: "ignored", detail: rawText };

    const learnedResponse = findLearnedResponse(brain, rawText);
    if (learnedResponse) {
      say(learnedResponse, "Trained response");
      result = { intent: "trained_response", status: "done", detail: learnedResponse };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── ANY-PAGE START / STOP CONTEXT ──
    if (
      isPageActionCommand(text, brain) &&
      !/\b(face|camera|scan|nexo|assistant)\b/.test(text) &&
      !(/\b(expiry|decay|sensex|itm terminal|sensex itm)\b/.test(text) && /\b(start|begin|run|launch|activate)\b/.test(text))
    ) {
      const dest = findNavDestination(text, brain);
      const contextPage = dest || activePageRef.current || pageForPath(window.location.pathname);
      const action = isStopPageAction(text, brain) ? "stop" : "start";

      if (dest && window.location.pathname !== dest.path) {
        say(`${action === "start" ? "Opening" : "Opening"} ${pathLabel(dest.path)} first.`, `Navigating → ${dest.path}`);
        activePageRef.current = dest;
        setActivePageContext(dest);
        localStorage.setItem(PAGE_CONTEXT_KEY, JSON.stringify({ ...dest, updatedAt: Date.now() }));
        navigate(dest.path);
        await waitForPagePaint();
      }

      const clickedSequence = await clickCommandControlSequence(trainedText, brain);
      const outcome = await performPageAction(trainedText, contextPage, brain);
      if (outcome.clicked) {
        const sequenceLabel = clickedSequence.length ? ` after selecting ${clickedSequence.join(" → ")}` : "";
        say(`${action === "start" ? "Started" : "Stopped"} ${outcome.target}${sequenceLabel} Boss.`, `${action === "start" ? "Started" : "Stopped"}: ${outcome.clicked}`);
        result = { intent: `page_${action}`, status: "done", detail: `${contextPage.path}: ${[...clickedSequence, outcome.clicked].join(" → ")}` };
      } else {
        say(`I could not find a visible ${action} control on ${pathLabel(contextPage.path)}.`, `${action} control not found`);
        result = { intent: `page_${action}`, status: "failed", detail: `${contextPage.path}: control not found` };
      }

      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── EXPIRY DECAY DESK ADAPTER ──
    if (/\b(expiry|decay|sensex|itm terminal|sensex itm)\b/.test(text)) {
      if (/\b(start|begin|run|launch|activate)\b/.test(text) && /\b(sensex|itm)\b/.test(text)) {
        say("Starting Sensex ITM terminal.", "Opening Expiry Decay Desk → SENSEX ITM");
        result = { intent: "expiry_decay_start_sensex_itm", status: "running", detail: rawText };
        logCommand({ transcript: rawText, ...result });

        if (window.location.pathname !== "/expiry-decay-desk") {
          navigate("/expiry-decay-desk");
          await delay(650);
        }

        clickVisibleButtonByKeywords(["sensex"], {});
        await delay(300);
        clickVisibleButtonByKeywords(["sensex itm", "itm"], {});
        await delay(300);
        const clicked = clickVisibleButtonByKeywords(["start", "start sensex", "▶ start"], {
          preferStart: true,
          rejectStop: true,
        }) || clickVisibleElement("start");

        if (clicked) {
          say("Sensex ITM terminal started Boss.", `Started: ${clicked === true ? "START" : clicked}`);
          result = { intent: "expiry_decay_start_sensex_itm", status: "done", detail: clicked === true ? "START" : clicked };
        } else {
          say("I opened Sensex ITM, but could not find the visible start button.", "Start button not found");
          result = { intent: "expiry_decay_start_sensex_itm", status: "failed", detail: "Start button not found" };
        }

        logCommand({ transcript: rawText, ...result });
        return;
      }

      if (isReadRequest(text) && /\b(volume|seller|sellers|call|put|ce|pe|data|oi|open interest|summary|details)\b/.test(text)) {
        const limit = configRef.current?.settings?.readLimit || 1200;
        const content = getExpiryDecayMetricText(rawText, limit);
        if (content) {
          say(content, "Reading Sensex ITM terminal data");
          result = { intent: "expiry_decay_read_metrics", status: "done", detail: content.slice(0, 220) };
        } else {
          say("I could not find Sensex ITM totals yet. Start the terminal and wait for data to load.", "Sensex ITM data not found");
          result = { intent: "expiry_decay_read_metrics", status: "failed", detail: "No matching metric blocks" };
        }
        logCommand({ transcript: rawText, ...result });
        return;
      }
    }

    // ── CONFIRMATION FLOW ──
    if (/^(confirm|yes confirm|yes do it|do it|proceed|execute)$/.test(text)) {
      const pending = pendingRef.current;
      if (pending) {
        setPendingCommand(null);
        pendingRef.current = null;
        logCommand({ transcript: rawText, intent: "confirm", status: "executing", detail: pending });
        return executeCommand(pending);
      }
      say("No pending command to confirm.");
      return;
    }

    if (/^(cancel|abort|stop command|nevermind|never mind)$/.test(text)) {
      setPendingCommand(null);
      pendingRef.current = null;
      say("Command cancelled.", "Command cancelled");
      logCommand({ transcript: rawText, intent: "cancel", status: "done", detail: "User cancelled pending command" });
      return;
    }

    // ── SENSITIVE COMMAND GUARD ──
    const cfg = configRef.current;
    if (cfg.settings?.commandMode !== "instant" && SENSITIVE_PATTERN.test(text)) {
      setPendingCommand(rawText);
      pendingRef.current = rawText;
      say(`Sensitive command detected. Say confirm to execute: ${rawText}`, "⚠ Awaiting confirmation");
      logCommand({ transcript: rawText, intent: "sensitive_gate", status: "pending", detail: rawText });
      return;
    }

    // ── STOP AGENT ──
    if (/\b(stop nexo|stop assistant|shutdown nexo|nexo stop|nexo shutdown)\b/.test(text)) {
      say("Nexo shutting down. Goodbye Boss.", "Stopped");
      // stopAgent called via state
      localStorage.removeItem(AGENT_SESSION_KEY);
      localStorage.removeItem(AGENT_FACE_VERIFIED_KEY);
      setRunning(false);
      restartRef.current = false;
      recognitionRef.current?.stop?.();
      stopCamera();
      setVerified(false);
      verifiedRef.current = false;
      result = { intent: "stop", status: "done", detail: "Agent stopped by voice" };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── NAVIGATION ──
    if (!isSpecificReadRequest(text) && /\b(switch to|shift to|open|go to|navigate to|move to|take me to|jump to|load)\b/.test(text)) {
      const dest = findNavDestination(text, brain);
      if (dest) {
        activePageRef.current = dest;
        setActivePageContext(dest);
        localStorage.setItem(PAGE_CONTEXT_KEY, JSON.stringify({ ...dest, updatedAt: Date.now() }));
        say(`Opening ${pathLabel(dest.path)}. I will keep this page ready for start, stop, and read commands.`, `Navigating → ${dest.path}`);
        result = { intent: "navigate", status: "done", detail: dest.path };
        logCommand({ transcript: rawText, ...result });
        // Small delay so TTS fires before route change
        setTimeout(() => navigate(dest.path), 300);
      } else {
        const clickedTab = clickInternalTab(trainedText);
        if (clickedTab) {
          say(`Opened ${clickedTab}.`, `Internal tab → ${clickedTab}`);
          result = { intent: "internal_tab", status: "done", detail: clickedTab };
          logCommand({ transcript: rawText, ...result });
          return;
        }
        const suggested = findBestVisibleControl(trainedText, 0.36);
        if (suggested) {
          const pageLabel = pathLabel(activePageRef.current?.path || window.location.pathname);
          const internalContext = activeInternalContextLabel();
          const pending = `click ${suggested.label}`;
          setPendingCommand(pending);
          pendingRef.current = pending;
          say(
            `I heard "${rawText}" as "${text}". Shall I open ${suggested.label}${internalContext ? ` under ${pageLabel}, ${internalContext}` : ` on ${pageLabel}`}? Say confirm to do it.`,
            `Suggestion → ${suggested.label}`
          );
          result = { intent: "suggest_control", status: "pending", detail: `${pageLabel}: ${internalContext || "current view"} → ${suggested.label}` };
          logCommand({ transcript: rawText, ...result });
          return;
        }
        say("I could not find that page. Check page aliases in the assistant settings.", "Navigation failed");
        result = { intent: "navigate", status: "failed", detail: rawText };
        logCommand({ transcript: rawText, ...result });
      }
      return;
    }

    // ── CLICK ──
    if (/^(click|press|tap|hit|select)\b/.test(text)) {
      const payload = stripCommandWord(trainedText, "click", "press", "tap", "hit", "select");
      if (/^select\b/.test(text)) {
        const selected = selectDropdownOption(trainedText);
        if (selected) {
          say(`Selected ${selected}.`, `Selected → ${selected}`);
          result = { intent: "select_option", status: "done", detail: selected };
          logCommand({ transcript: rawText, ...result });
          return;
        }
      }
      const internalClicked = clickInternalTab(payload);
      if (internalClicked) {
        say(`Opened ${internalClicked}.`, `Internal tab → ${internalClicked}`);
        result = { intent: "internal_tab", status: "done", detail: internalClicked };
        logCommand({ transcript: rawText, ...result });
        return;
      }
      const clicked = clickVisibleElementFuzzy(payload);
      if (clicked) {
        say("Clicked.", `Clicked: ${clicked === true ? payload : clicked}`);
        result = { intent: "click", status: "done", detail: clicked === true ? payload : clicked };
      } else {
        say(`Could not find a visible control matching "${payload}".`, "Click failed");
        result = { intent: "click", status: "failed", detail: payload };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── INTERNAL TAB / SECTION ──
    if (/\b(tab|internal tab|section|under|inside)\b/.test(text) && !isReadRequest(text)) {
      const clicked = clickInternalTab(trainedText);
      if (clicked) {
        say(`Opened ${clicked}.`, `Internal tab → ${clicked}`);
        result = { intent: "internal_tab", status: "done", detail: clicked };
        logCommand({ transcript: rawText, ...result });
        return;
      }
    }

    // ── DROPDOWN / OPTION SELECTION ──
    if (/\b(select|choose|pick|set|dropdown|drop down|expiry date|date option|option)\b/.test(text) && !isReadRequest(text)) {
      const selected = selectDropdownOption(trainedText);
      if (selected) {
        say(`Selected ${selected}.`, `Selected → ${selected}`);
        result = { intent: "select_option", status: "done", detail: selected };
        logCommand({ transcript: rawText, ...result });
        return;
      }
    }

    // ── TYPE / WRITE ──
    if (/^(type|write)\b/.test(text)) {
      const payload = stripCommandWord(rawText, "type", "write");
      const target = activeTextTarget();
      if (!target) {
        say("Please click inside a text field first.", "No active text field");
        result = { intent: "type", status: "failed", detail: "No active text target" };
      } else if (setTargetValue(target, payload, true)) {
        say("Typed.", `Typed: "${payload.slice(0, 40)}"`);
        result = { intent: "type", status: "done", detail: payload };
      } else {
        say("Could not type into that field.", "Type failed");
        result = { intent: "type", status: "failed", detail: payload };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── PASTE ──
    if (/^paste\b/.test(text)) {
      const payload = stripCommandWord(rawText, "paste");
      const target = activeTextTarget();
      if (!target) {
        say("Click inside a text field first.", "No active text field");
        result = { intent: "paste", status: "failed", detail: "No active text target" };
      } else if (setTargetValue(target, payload, true)) {
        say("Pasted.", `Pasted: "${payload.slice(0, 40)}"`);
        result = { intent: "paste", status: "done", detail: payload };
      } else {
        result = { intent: "paste", status: "failed", detail: payload };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── CLEAR FIELD ──
    if (/^(clear|erase)\b/.test(text) && /\b(field|text|box|input)\b/.test(text)) {
      const target = activeTextTarget();
      if (setTargetValue(target, "", false)) {
        say("Field cleared.", "Cleared field");
        result = { intent: "clear_text", status: "done", detail: "Cleared active field" };
      } else {
        say("Click inside a text field first.", "Clear failed");
        result = { intent: "clear_text", status: "failed", detail: "No active target" };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── SUBMIT / ENTER ──
    if (/\b(done enter|submit|send message|press enter|hit enter|enter)\b/.test(text)) {
      const target = activeTextTarget();
      if (submitNearestForm(target)) {
        say("Submitted.", "Submitted ✓");
        result = { intent: "submit", status: "done", detail: "Submitted form or field" };
      } else {
        say("Could not find a submit action.", "Submit failed");
        result = { intent: "submit", status: "failed", detail: "No submit target" };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── SCROLL ──
    if (/\b(scroll down|scroll up|scroll top|scroll bottom|page down|page up)\b/.test(text)) {
      if (/down/.test(text)) window.scrollBy({ top: 400, behavior: "smooth" });
      else if (/up/.test(text)) window.scrollBy({ top: -400, behavior: "smooth" });
      else if (/top/.test(text)) window.scrollTo({ top: 0, behavior: "smooth" });
      else if (/bottom/.test(text)) window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      say("Scrolled.", "Scrolled page");
      result = { intent: "scroll", status: "done", detail: text };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── READ PAGE ──
    if (isReadRequest(text)) {
      const limit = configRef.current?.settings?.readLimit || 1200;
      const topic = extractReadTopic(rawText);
      const expiryMetricText = window.location.pathname === "/expiry-decay-desk"
        ? getExpiryDecayMetricText(trainedText, limit)
        : "";
      const genericMetricText = getGenericMetricText(trainedText, limit);
      const content = expiryMetricText || genericMetricText || getSpecificPageText(trainedText, limit);
      if (content) {
        say(content, topic ? `Reading ${pathLabel(activePageRef.current?.path || window.location.pathname)}: ${topic}` : "Reading page content…");
        result = {
          intent: "read_page",
          status: "done",
          detail: topic ? `Read ${content.length} chars for ${topic}` : `Read ${content.length} chars`,
        };
      } else {
        say("No readable news, data, or page text found here.", "Nothing to read");
        result = { intent: "read_page", status: "failed", detail: topic ? `No readable text for ${topic}` : "Empty page text" };
      }
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── FACE SCAN ──
    if (/\b(start face scan|scan my face|face scan|start eye scan|scan my eyes|take photo|click photo|capture photo)\b/.test(text)) {
      await startCamera();
      say("Face scan camera started.", "Face scan active");
      result = { intent: "face_scan_start", status: "done", detail: "Camera started" };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    if (/\b(capture face|capture eyes|capture scan|save face scan|eye scan now)\b/.test(text)) {
      await captureFaceScan("voice");
      result = { intent: "face_eye_scan", status: "done", detail: "Captured and saved" };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    if (/\b(stop face|stop camera|close camera|stop scan)\b/.test(text)) {
      stopCamera();
      say("Camera stopped.", "Camera off");
      result = { intent: "face_scan_stop", status: "done", detail: "Camera stopped" };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── RELOAD PAGE ──
    if (/\b(reload|refresh)\b/.test(text)) {
      say("Reloading page.", "Reloading…");
      setTimeout(() => window.location.reload(), 600);
      result = { intent: "reload", status: "done", detail: "Page reload triggered" };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── WHAT TIME ──
    if (/\b(what time|current time|time now)\b/.test(text)) {
      const t = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
      say(`The current time is ${t}.`, `Time: ${t}`);
      result = { intent: "time_query", status: "done", detail: t };
      logCommand({ transcript: rawText, ...result });
      return;
    }



    // ── NATURAL LANGUAGE FALLBACK ──
    const dest = findNavDestination(text, brain);
    if (dest) {
      activePageRef.current = dest;
      setActivePageContext(dest);
      localStorage.setItem(PAGE_CONTEXT_KEY, JSON.stringify({ ...dest, updatedAt: Date.now() }));
      say(`Opening ${pathLabel(dest.path)}.`, `Navigating → ${dest.path}`);
      result = { intent: "navigate", status: "done", detail: dest.path };
      logCommand({ transcript: rawText, ...result });
      setTimeout(() => navigate(dest.path), 300);
      return;
    }

    const clickWords = text
      .replace(/\b(can you|please|nexo|start|open|launch|activate|begin|initiate|turn on|enable)\b/g, "")
      .trim();
    const clicked = clickWords.length > 2 ? clickVisibleElementFuzzy(clickWords, 0.5) : clickVisibleElementFuzzy(text, 0.5);
    if (clicked) {
      say("Done Boss.", `Clicked: ${clicked === true ? clickWords || text : clicked}`);
      result = { intent: "click", status: "done", detail: clicked === true ? clickWords || text : clicked };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    const suggested = findBestVisibleControl(trainedText, 0.38);
    if (suggested) {
      const pageLabel = pathLabel(activePageRef.current?.path || window.location.pathname);
      const internalContext = activeInternalContextLabel();
      const pending = `click ${suggested.label}`;
      setPendingCommand(pending);
      pendingRef.current = pending;
      say(
        `I heard "${rawText}" as "${text}". Shall I open ${suggested.label}${internalContext ? ` under ${pageLabel}, ${internalContext}` : ` on ${pageLabel}`}? Say confirm to do it.`,
        `Suggestion → ${suggested.label}`
      );
      result = { intent: "suggest_control", status: "pending", detail: `${pageLabel}: ${internalContext || "current view"} → ${suggested.label}` };
      logCommand({ transcript: rawText, ...result });
      return;
    }

    // ── UNRECOGNISED — with failure escalation ──
    const failCount = recordRecognitionFailure(rawText);
    if (shouldEscalateToManualEntry(rawText)) {
      say("I am having trouble understanding that phrase. Please try the typed command bar, or speak it slowly word by word.", "⚠ Repeated mis-hear — try typed input");
      result = { intent: "escalate_manual", status: "failed", detail: `${failCount} failures: ${rawText}` };
    } else {
      say("Command not recognised. Try saying click, type, shift to, or read out.", "Unknown command");
      result = { intent: "unknown", status: "ignored", detail: rawText };
    }
    logCommand({ transcript: rawText, ...result });
  }, [captureFaceScan, logCommand, navigate, speak, startCamera, stopCamera]);

  // ── Transcript Handler ────────────────────────────────────────────────────────
  const handleTranscript = useCallback((transcript) => {
    setLastTranscript(transcript);
    setWaveActive(true);
    setTimeout(() => setWaveActive(false), 1200);

    const cfg = configRef.current;
    const enrolledVoiceTone = (cfg.voiceProfiles || []).some(p => p.active !== false && p.voiceprint?.bands?.length);
    if (cfg.settings?.requireVoiceTone !== false && enrolledVoiceTone) {
      if (!liveVoiceRef.current || voiceSamplesRef.current.length < 5) {
        setStatus("Listening — calibrating voice tone…");
      } else {
        const phraseMatched = (cfg.voiceProfiles || [])
          .filter(p => p.active !== false && p.phrase?.trim())
          .some(p => correctSpokenText(transcript, commandBrainRef.current).includes(correctSpokenText(p.phrase, commandBrainRef.current)));
        const match = bestVoiceToneMatch(liveVoiceRef.current, cfg.voiceProfiles || []);
        const graceOk = phraseMatched && match.score >= VOICE_TONE_PHRASE_GRACE_THRESHOLD;
        if (!match.ok && !graceOk) {
          setStatus(`Voice tone blocked — speaker mismatch (${match.score || 0}%)`);
          speak("Voice tone not recognised. I will only respond to enrolled voice profiles.", true);
          logCommand({ transcript, intent: "voice_tone_gate", status: "blocked", detail: `${match.label}: ${match.score || 0}%` });
          return;
        }
      }
    }

    if (!verifiedRef.current && FACE_COMMAND_PATTERN.test(normalize(transcript))) {
      executeCommand(transcript);
      return;
    }

    if (!verifiedRef.current) {
      if (verifyVoice(transcript)) {
        setVerified(true);
        verifiedRef.current = true;
        recordVoiceVerificationResult(true);
        setStatus("✓ Voice verified — listening for commands");
        speak("Voice verified. Ready for commands Boss.", true);
        logCommand({ transcript, intent: "voice_verify", status: "done", detail: "Passphrase matched" });
      } else {
        const flagged = recordVoiceVerificationResult(false);
        if (flagged) {
          logCommand({ transcript, intent: "security_anomaly", status: "flagged", detail: "Repeated voice mismatch — possible unauthorised use" });
        }
        setStatus("⚠ Voice not verified — say your passphrase");
        speak("Voice not recognised. Say your verification phrase.", true);
        logCommand({ transcript, intent: "voice_verify", status: "blocked", detail: "Passphrase mismatch" });
      }
      return;
    }

    executeCommand(transcript);
  }, [executeCommand, logCommand, speak, verifyVoice]);

  
  // ── Start Agent ───────────────────────────────────────────────────────────────
  const startAgent = useCallback(async (options = {}) => {
    if (runningRef.current) return;
    const resumeMode = options?.resume === true;
    const skipFaceScan = resumeMode && hasSessionFaceVerification();
    if (!resumeMode) localStorage.removeItem(AGENT_FACE_VERIFIED_KEY);
    localStorage.setItem(AGENT_SESSION_KEY, "1");

    // Tear down any previous recognition
    if (recognitionRef.current) {
      restartRef.current = false;
      recognitionRef.current.onresult = null;
      recognitionRef.current.onend = null;
      recognitionRef.current.onerror = null;
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }

    const rec = createRecognition();
    if (!rec) {
      localStorage.removeItem(AGENT_SESSION_KEY);
      setStatus("⚠ Speech recognition not supported in this browser");
      speak("Speech recognition is not supported. Use Chrome or Edge.", true);
      return;
    }

    const loadedConfig = await loadConfig();
    await loadCommandBrain();
    await startVoiceMonitor();
    const s = loadedConfig?.settings || {};
    const profiles = (loadedConfig?.voiceProfiles || []).filter(p => p.active !== false && p.phrase?.trim());
    const autoVerify = !s.requireVoiceProfile || profiles.length === 0;

    recognitionRef.current = rec;
    restartRef.current = true;

    rec.onstart = () => {
      setListening(true);
    };

    rec.onend = () => {
      setListening(false);
      if (restartRef.current) {
        setTimeout(() => {
          if (restartRef.current && recognitionRef.current) {
            try { recognitionRef.current.start(); } catch {}
          }
        }, 250);
      }
    };

    rec.onerror = (e) => {
      const map = {
        "not-allowed": "⚠ Microphone permission denied — check browser settings",
        "service-not-allowed": "⚠ Microphone service not allowed",
        "no-speech": "No speech detected — still listening…",
        "network": "Network error — speech service unavailable",
      };
      setStatus(map[e?.error] || `Listening error: ${e?.error || "unknown"}`);
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        localStorage.removeItem(AGENT_SESSION_KEY);
        restartRef.current = false;
        setRunning(false);
        setListening(false);
      }
    };

    rec.onresult = (e) => {
      let interim = "";
      let finalText = "";

      for (let resultIndex = e.resultIndex; resultIndex < e.results.length; resultIndex++) {
        const result = e.results[resultIndex];
        const best = chooseBestSpeechAlternative(Array.from(result), commandBrainRef.current);

        if (result.isFinal) finalText += ` ${best}`;
        else interim += ` ${best}`;
      }

      const preview = interim.trim();
      if (preview) {
        const now = Date.now();
        if (preview !== lastInterimRef.current.text || now - lastInterimRef.current.at > 350) {
          lastInterimRef.current = { text: preview, at: now };
          setLastTranscript(preview);
          setStatus(verifiedRef.current ? "Listening — catching your command…" : "Listening — waiting for verification phrase");
        }
      }

      const finalTranscript = finalText.trim();
      if (finalTranscript) {
        handleTranscript(finalTranscript);
        commandCountRef.current += 1;
        if (commandCountRef.current % 8 === 0) {
          clearTimeout(restartTimerRef.current);
          restartTimerRef.current = setTimeout(() => {
            if (restartRef.current && recognitionRef.current) {
              try { recognitionRef.current.stop(); } catch {}
            }
          }, 300);
        }
      }
    };

    setRunning(true);
    runningRef.current = true;
    setExpanded(true);

    // ── STARTUP ANOMALY CHECK ──
    if (checkStartupAnomalies()) {
      logCommand({ transcript: "system", intent: "security_anomaly", status: "flagged", detail: "Odd-hour agent activation" });
    }

    setVerified(autoVerify);
    verifiedRef.current = autoVerify;

    const greeting = s.bossGreeting || "Hello Boss. Nexo is online and ready.";
    const statusMsg = autoVerify ? "Listening — say a command" : "Listening — say your verification phrase";
    setStatus(statusMsg);
    speak(greeting, true);

    if (skipFaceScan) {
      setVerified(true);
      verifiedRef.current = true;
      setStatus("✓ Nexo resumed — face already verified this session");
    } else {
      // Manual starts verify face again; refresh resumes skip this after one successful scan.
      await startCamera();
      setTimeout(async () => {
        const scanOk = await captureFaceScan("auto_verify");
        const cfg = configRef.current;
        const enrolled = cfg.faceProfiles || [];

        if (scanOk) {
          localStorage.setItem(AGENT_FACE_VERIFIED_KEY, "1");
          if (enrolled.length > 0) {
            speak("Face verified. Your orders Boss.", true);
            setStatus("✓ Face verified — awaiting your command");
          } else {
            speak("Face scanned. No profiles enrolled yet. Ready for your orders Boss.", true);
            setStatus("✓ Ready — enroll a face profile in the desk for identity lock");
          }
        } else {
          localStorage.removeItem(AGENT_FACE_VERIFIED_KEY);
          speak("Face scan failed. Please ensure camera is clear and try again.", true);
          setStatus("⚠ Face scan failed — retry or check camera");
        }
      }, 800);
    }

    try {
      rec.start();
    } catch {
      setStatus("Already listening");
    }
  }, [captureFaceScan, handleTranscript, loadCommandBrain, loadConfig, logCommand, speak, startCamera, startVoiceMonitor]);

  // ── Stop Agent ────────────────────────────────────────────────────────────────
  const stopAgent = useCallback(() => {
    localStorage.removeItem(AGENT_SESSION_KEY);
    localStorage.removeItem(AGENT_FACE_VERIFIED_KEY);
    clearTimeout(restartTimerRef.current);
    commandCountRef.current = 0;
    restartRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onend = null;
      try { recognitionRef.current.stop(); } catch {}
    }
    window.speechSynthesis?.cancel?.();
    stopVoiceMonitor();
    stopCamera();
    setRunning(false);
    runningRef.current = false;
    setListening(false);
    setVerified(false);
    verifiedRef.current = false;
    setPendingCommand(null);
    pendingRef.current = null;
    setStatus("Stopped — click orb to restart");
  }, [stopCamera, stopVoiceMonitor]);

  useEffect(() => {
    if (shouldResumeAgent() && !runningRef.current) {
      const t = setTimeout(() => startAgent({ resume: true }), 500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [startAgent]);

  useEffect(() => {
    const resumeIfNeeded = () => {
      if (shouldResumeAgent() && !runningRef.current && document.visibilityState === "visible") {
        startAgent({ resume: true });
      } else if (shouldResumeAgent() && runningRef.current && restartRef.current && recognitionRef.current && !listening) {
        try { recognitionRef.current.start(); } catch {}
      }
    };

    window.addEventListener("focus", resumeIfNeeded);
    window.addEventListener("pageshow", resumeIfNeeded);
    document.addEventListener("visibilitychange", resumeIfNeeded);
    return () => {
      window.removeEventListener("focus", resumeIfNeeded);
      window.removeEventListener("pageshow", resumeIfNeeded);
      document.removeEventListener("visibilitychange", resumeIfNeeded);
    };
  }, [listening, startAgent]);

  // Cleanup recognition/camera handles on unmount without clearing the resume flag.
  useEffect(() => () => {
    clearTimeout(restartTimerRef.current);
    restartRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onend = null;
      recognitionRef.current.onerror = null;
      try { recognitionRef.current.stop(); } catch {}
    }
    stopVoiceMonitor();
    stopCamera();
  }, [stopCamera, stopVoiceMonitor]);

  // ── Render ────────────────────────────────────────────────────────────────────
  const orbGlow = running
    ? listening
      ? "0 0 32px rgba(34,197,94,.7), 0 0 60px rgba(34,197,94,.3)"
      : "0 0 24px rgba(45,212,191,.48)"
    : "0 12px 30px rgba(0,0,0,.35)";

  const orbBg = running
    ? listening ? "#166534" : "#0f766e"
    : "#0f172a";

  return (
    <div style={{ position: "fixed", right: 20, bottom: 20, zIndex: 9999, fontFamily: "Inter, system-ui, sans-serif" }}>

      {/* ── EXPANDED PANEL ── */}
      {expanded && (
        <div style={panelStyle}>

          {/* Header */}
          <div style={headerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14 }}>
              <Bot size={18} color={running ? "#22c55e" : "#64748b"} />
              {config.settings?.assistantName || "Nexo"} Agent
              {running && <span style={pulseDot} />}
            </div>
            <button onClick={() => setExpanded(false)} style={iconBtnStyle} title="Minimise">
              <X size={16} />
            </button>
          </div>

          {/* Status tiles */}
          <div style={{ padding: "10px 14px 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <Tile icon={<Mic size={13} />} label={listening ? "Listening" : "Idle"} active={listening} color="#22c55e" />
            <Tile icon={<Check size={13} />} label={verified ? "Verified" : "Locked"} active={verified} color="#38bdf8" />
            <Tile icon={<ScanFace size={13} />} label={faceReady ? "Cam On" : "Cam Off"} active={faceReady} color="#a78bfa" />
          </div>

          {/* Status bar */}
          <div style={{ padding: "8px 14px 0" }}>
            <div style={{ fontSize: 12, color: "#bfdbfe", minHeight: 30, lineHeight: 1.5 }}>{status}</div>
            {activePageContext?.path && (
              <div style={{ fontSize: 10, color: "#67e8f9", marginTop: 3, letterSpacing: 1 }}>
                CONTEXT: {pathLabel(activePageContext.path).toUpperCase()}
              </div>
            )}
            {scanStatus && <div style={{ fontSize: 11, color: "#99f6e4", marginTop: 4, lineHeight: 1.4 }}>{scanStatus}</div>}
            {lastTranscript && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <Mic size={11} />
                <span style={{ fontStyle: "italic" }}>{lastTranscript.slice(0, 60)}{lastTranscript.length > 60 ? "…" : ""}</span>
              </div>
            )}
            {pendingCommand && (
              <div style={{ marginTop: 6, background: "#451a03", border: "1px solid #92400e", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#fbbf24", display: "flex", alignItems: "flex-start", gap: 6 }}>
                <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>Pending: <em>{pendingCommand}</em><br />Say <strong>confirm</strong> to execute or <strong>cancel</strong></span>
              </div>
            )}
          </div>

          {/* Controls */}
          <div style={{ padding: "10px 14px", display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              {!running ? (
                <button onClick={startAgent} style={primaryBtn}><Play size={15} /> Start Nexo</button>
              ) : (
                <button onClick={stopAgent} style={dangerBtn}><Pause size={15} /> Stop</button>
              )}
              <button onClick={() => navigate("/nexo-assistant")} style={secondaryBtn} title="Open desk"><Navigation size={15} /></button>
              <button onClick={() => speak(getPageText(500) || "Nothing readable.", true)} style={secondaryBtn} title="Read page"><Volume2 size={15} /></button>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={startCamera} style={secondaryBtnFull}><Camera size={14} /> Camera</button>
              <button onClick={() => captureFaceScan("button")} style={secondaryBtnFull}><ScanFace size={14} /> Capture</button>
            </div>

            {/* Hint bar */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, color: "#64748b", fontSize: 10, paddingTop: 2 }}>
              <span><Keyboard size={11} /> "type / paste …"</span>
              <span><Navigation size={11} /> "shift to [tab]"</span>
              <span><MousePointerClick size={11} /> "click [button]"</span>
              <span><ShieldCheck size={11} /> "confirm / cancel"</span>
            </div>

            {/* Command history micro-log */}
            {commandHistory.length > 0 && (
              <div style={{ borderTop: "1px solid #1f3a5f", paddingTop: 8, maxHeight: 100, overflowY: "auto" }}>
                {commandHistory.slice(0, 5).map((c, i) => (
                  <div key={i} style={{ fontSize: 10, color: c.status === "done" ? "#4ade80" : c.status === "failed" ? "#f87171" : "#94a3b8", marginBottom: 3 }}>
                    <span style={{ color: "#475569", marginRight: 4 }}>{c.ts}</span>
                    <span>{c.intent}</span> — <span style={{ fontStyle: "italic" }}>{String(c.detail || "").slice(0, 40)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Face video (hidden when camera off) */}
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              display: faceReady ? "block" : "none",
              width: "100%",
              height: 100,
              objectFit: "cover",
              borderTop: "1px solid #1f3a5f",
            }}
          />
        </div>
      )}

      {/* ── ORB BUTTON ── */}
      <button
        onClick={() => running ? setExpanded(v => !v) : startAgent()}
        title={running ? "Toggle Nexo panel" : "Start Nexo"}
        style={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          border: `2px solid ${running ? (listening ? "#22c55e" : "#2dd4bf") : "#334155"}`,
          background: orbBg,
          color: "#fff",
          display: "grid",
          placeItems: "center",
          boxShadow: orbGlow,
          cursor: "pointer",
          transition: "all 0.3s ease",
          position: "relative",
        }}
      >
        {running ? <Mic size={26} color={listening ? "#22c55e" : "#fff"} /> : <MicOff size={24} color="#64748b" />}
        {waveActive && <span style={waveRing} />}
      </button>
    </div>
  );
}

// ── Sub-component ─────────────────────────────────────────────────────────────
function Tile({ icon, label, active, color }) {
  return (
    <div style={{
      border: `1px solid ${active ? color + "55" : "#1f3a5f"}`,
      borderRadius: 6,
      padding: "7px 4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      fontSize: 10,
      color: active ? color : "#475569",
      background: active ? color + "11" : "#0f1d32",
      transition: "all 0.25s ease",
    }}>
      {icon}{label}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const panelStyle = {
  width: 340,
  marginBottom: 12,
  background: "#08111f",
  color: "#e5eefb",
  border: "1px solid #1f3a5f",
  borderRadius: 12,
  boxShadow: "0 24px 60px rgba(0,0,0,.55)",
  overflow: "hidden",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 14px",
  borderBottom: "1px solid #1f3a5f",
  background: "#0c1728",
};

const iconBtnStyle = {
  background: "transparent",
  border: 0,
  color: "#64748b",
  cursor: "pointer",
  padding: 4,
  borderRadius: 4,
  display: "grid",
  placeItems: "center",
};

const pulseDot = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#22c55e",
  display: "inline-block",
  animation: "pulse 1.5s infinite",
};

const waveRing = {
  position: "absolute",
  inset: -4,
  borderRadius: "50%",
  border: "2px solid #22c55e44",
  animation: "wave 1s ease-out forwards",
};

const base = {
  border: 0,
  borderRadius: 7,
  padding: "9px 12px",
  fontWeight: 700,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  cursor: "pointer",
  transition: "opacity 0.2s",
};

const primaryBtn = { ...base, flex: 1, background: "#16a34a", color: "#fff" };
const dangerBtn = { ...base, flex: 1, background: "#dc2626", color: "#fff" };
const secondaryBtn = { ...base, background: "#1e293b", border: "1px solid #334155", color: "#cbd5e1", width: 42, padding: 0 };
const secondaryBtnFull = { ...base, flex: 1, background: "#1e293b", border: "1px solid #334155", color: "#cbd5e1", fontSize: 12 };
